import { DRIFT_SENTENCE, VERBS, cents, signed } from "./shared";

export type BoundRecord = {
  readonly qboId: string;
  readonly entityType: string;
  readonly amountCents: number;
  readonly txnDate?: string | null;
  readonly counterparty?: string | null;
  readonly deltaCents: number;
  readonly manual: boolean;
  readonly txn: { readonly id: string; readonly amount: number; readonly postingDate?: string | null; readonly description?: string | null };
};

// Records the union bound at a DIFFERENT amount — near twins it claimed by
// itself on wallet accounts, or a person matched with a delta. Always one click
// from undo, because an automatic claim must never be silent.
export type Reversal = {
  readonly purchaseQboId: string;
  readonly depositQboId: string;
  readonly txnDate?: string | null;
  readonly amountCents: number;
  readonly counterparty?: string | null;
  readonly category?: string | null;
};

export function AutoMatchedTab({
  records,
  reversals,
  busy,
  onUnbind,
  onNotReversal,
}: {
  records: ReadonlyArray<BoundRecord>;
  reversals: ReadonlyArray<Reversal>;
  busy: boolean;
  onUnbind: (record: BoundRecord) => void;
  onNotReversal: (reversal: Reversal) => void;
}) {
  return (
    <div className="space-y-4">
    {reversals.length > 0 && (
      <section className="rounded border bg-white">
        <p className="border-b px-4 py-3 text-sm text-gray-600">
          <span className="font-medium text-gray-800">Reversed in QuickBooks.</span> Each of these is a Purchase and a Deposit of the
          same amount, same day, same payee, on the same QuickBooks account — a booking that was voided and re-entered. They cancel out
          in QuickBooks, so neither leg counts here (a cost line only sums outflows; the Deposit would never have offset the Purchase).
          If one is two real movements, keep both.
        </p>
        <ul className="divide-y">
          {reversals.map((reversal) => (
            <li key={`${reversal.purchaseQboId}:${reversal.depositQboId}`} className="flex flex-wrap items-center gap-3 px-4 py-2 text-xs text-gray-700">
              <span className="tabular-nums text-gray-500">{reversal.txnDate ?? "—"}</span>
              <span className="max-w-[18rem] truncate">{reversal.counterparty ?? "—"}</span>
              <span className="text-gray-400">{reversal.category ?? ""}</span>
              <span className="tabular-nums">
                Purchase #{reversal.purchaseQboId} {cents(reversal.amountCents)}
              </span>
              <span className="text-gray-400">↔</span>
              <span className="tabular-nums">
                Deposit #{reversal.depositQboId} {cents(-reversal.amountCents)}
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() => onNotReversal(reversal)}
                title="Keep both records in your books as two real movements"
                className="ml-auto rounded border px-2 py-0.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Not a reversal
              </button>
            </li>
          ))}
        </ul>
      </section>
    )}
    <section className="rounded border bg-white">
      <p className="border-b px-4 py-3 text-sm text-gray-600">
        {DRIFT_SENTENCE} These QuickBooks records were linked to a bank or wallet row at a slightly different amount. If one is
        two different payments, unlink it — it comes back as its own cost and is not re-linked at a different amount.
      </p>
      {records.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-400">Nothing linked at a different amount.</p>
      ) : (
        <ul className="divide-y">
          {records.map((record) => (
            <li key={`${record.entityType}:${record.qboId}`} className="flex flex-wrap items-center gap-3 px-4 py-2 text-xs text-gray-700">
              <span className="tabular-nums text-gray-500">{record.txnDate ?? "—"}</span>
              <span className="max-w-[18rem] truncate">{record.counterparty ?? "—"}</span>
              <span className="tabular-nums">QBO {cents(record.amountCents)}</span>
              <span className="text-gray-400">→</span>
              <span className="max-w-[20rem] truncate">{record.txn.description ?? "—"}</span>
              <span className="tabular-nums">{cents(record.txn.amount)}</span>
              <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] tabular-nums text-amber-800">Δ {signed(record.deltaCents)}</span>
              <span className={`rounded px-1.5 py-0.5 text-[11px] ${record.manual ? "bg-gray-100 text-gray-600" : "bg-indigo-50 text-indigo-700"}`}>
                {record.manual ? "matched by you" : "matched automatically"}
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() => onUnbind(record)}
                title={VERBS.UNBIND.consequence}
                className="ml-auto rounded border px-2 py-0.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {VERBS.UNBIND.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
    </div>
  );
}
